import React, {AriaAttributes} from 'react';

import {IconData, UserAvatarSize, useActionHandlers} from '@gravity-ui/uikit';

import {block} from '../../cn';
import {UserDropdownAvatar} from '../UserDropdownAvatar/UserDropdownAvatar';
import {isInviteeProps} from '../UserDropdownAvatar/helpers';
import {User as UserModel} from '../models';

import './UserDropdownUser.scss';

const b = block('user-dropdown-user');

export interface UserDropdownUserProps {
    user: UserModel;
    className?: string;
    internal?: boolean;
    name?: string;
    description?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    currentOrgId?: string;
    size?: UserAvatarSize;
    ariaAttributes?: AriaAttributes;
    organizationIcon?: IconData;
    organizationIconSize?: number;
}

export interface UserDropdownInviteeProps {
    invitee: true;
    className?: string;
    name?: string;
    description?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    size?: UserAvatarSize;
    ariaAttributes?: AriaAttributes;
}

export const UserDropdownUser = React.forwardRef<
    HTMLDivElement,
    UserDropdownUserProps | UserDropdownInviteeProps
>((props, ref) => {
    const {className, name, description, onClick, size, ariaAttributes} = props;
    const invitee = isInviteeProps(props);
    const user = (props as UserDropdownUserProps).user;
    const internal = (props as UserDropdownUserProps).internal;
    const currentOrgId = (props as UserDropdownUserProps).currentOrgId;
    const organizationIcon = (props as UserDropdownUserProps).organizationIcon;
    const organizationIconSize = (props as UserDropdownUserProps).organizationIconSize;
    const isClickable = Boolean(onClick);

    const {onKeyDown} = useActionHandlers(onClick);

    const userDropdownAvatarProps = invitee
        ? {invitee, className: b('avatar')}
        : {
              user,
              className: b('avatar'),
              internal,
              federation: (user as UserDropdownUserProps['user']).isFederationUser,
              currentOrgId,
              organizationIcon,
              organizationIconSize,
          };

    return (
        <div
            className={b(null, className)}
            role={isClickable ? 'button' : undefined}
            onClick={onClick}
            onKeyDown={isClickable ? onKeyDown : undefined}
            tabIndex={isClickable ? 0 : undefined}
            ref={ref}
            {...ariaAttributes}
        >
            <UserDropdownAvatar {...userDropdownAvatarProps} size={size} />
            {(name || description) && (
                <div className={b('info')}>
                    {name && <span className={b('name')}>{name}</span>}
                    {description && <span className={b('description')}>{description}</span>}
                </div>
            )}
        </div>
    );
});
